import React, { useEffect } from 'react';
import Button from '../../../../components/ui/button';

/**
 * Props for the RemoveStopDialog component
 */
interface RemoveStopDialogProps {
  /** The stop being removed (contains location info for confirmation) */
  stop: {
    order: number;
    location: string;
    location_country: string;
  };
  /** Callback when user confirms the removal */
  onConfirm: () => void;
  /** Callback when user cancels the removal */
  onCancel: () => void;
}

/**
 * RemoveStopDialog Component
 * 
 * A confirmation dialog for removing a stop from the journey.
 * Displays the stop's location to help user confirm they're removing the right stop.
 * 
 * Features:
 * - Clear confirmation message with stop details
 * - Keyboard shortcuts (Enter to confirm, Escape to cancel)
 * - Prevents background scrolling while open
 * - Accessible with proper ARIA labels
 * 
 * @example
 * <RemoveStopDialog
 *   stop={{ order: 3, location: 'Berlin', location_country: 'Germany' }}
 *   onConfirm={() => handleRemove()}
 *   onCancel={() => setDialogOpen(false)}
 * />
 */
const RemoveStopDialog: React.FC<RemoveStopDialogProps> = ({
  stop,
  onConfirm,
  onCancel
}) => {
  /**
   * Handles keyboard shortcuts
   * - Enter: Confirm removal
   * - Escape: Cancel
   */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onConfirm();
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  /**
   * Prevents background scrolling while dialog is open
   * Restores scrolling on unmount
   */
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onCancel}
    >
      <div
        role="dialog"
        aria-labelledby="remove-dialog-title"
        aria-describedby="remove-dialog-description"
        aria-modal="true"
        className="bg-white rounded-lg p-6 w-96 max-w-full mx-4"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        {/* Dialog Header */}
        <h3 id="remove-dialog-title" className="text-lg font-medium mb-2 text-red-600">
          Remove Stop?
        </h3>

        {/* Dialog Content */}
        <div id="remove-dialog-description" className="space-y-4">
          <p className="text-gray-700">
            Are you sure you want to remove{' '}
            <strong>{stop.location}, {stop.location_country}</strong>{' '}
            (Stop #{stop.order}) from your journey?
          </p>
          
          <p className="text-sm text-gray-600">
            This will also remove the transport legs connected to this stop.
            You can undo this by discarding your changes.
          </p>

          {/* Dialog Actions */}
          <div className="flex justify-end space-x-2 pt-2">
            <Button
              variant="secondary"
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={onConfirm}
            >
              Remove Stop
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RemoveStopDialog;

